import { useState } from "react"
import { assignCodesToGroup, createCodeGroup } from "../../lib/api"
import { cleanMessage } from "./GroupsDialog"

const NEW_GROUP = "__new__"
const NO_GROUP  = "__none__"

// The bar that appears while codes are selected in YourCodesCard: how many are
// selected, "select all N matching" once the whole page is ticked, a Move-to
// control (a group, no group, or a new group created on the spot), and Change
// destination, which hands the same selection to RepointDialog.
//
// `selection` is { tokens: Set } for an explicit pick, or { all: true } for
// everything matching the list's current search + group filter — which the
// server resolves itself (`match`), so moving 153 codes doesn't mean paging.
export default function SelectionBar({ selection, pageTokens, total, filter, groups, onSelectAll, onClear, onMoved, onRepoint }) {
  const [target, setTarget] = useState("")
  const [newName, setNewName] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const count = selection.all ? total : selection.tokens.size
  const pageFullySelected = !selection.all && pageTokens.length > 0 && pageTokens.every((t) => selection.tokens.has(t))
  const offerAll = pageFullySelected && total > pageTokens.length

  async function move() {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      let groupId = null
      let groupName = null
      if (target === NEW_GROUP) {
        const created = await createCodeGroup(newName.trim())
        groupId = created.id
        groupName = created.name
      } else if (target !== NO_GROUP) {
        groupId = Number(target)
        groupName = groups.find((g) => g.id === groupId)?.name || "that group"
      }
      const request = selection.all
        ? { groupId, match: { q: filter.q, group: filter.group } }
        : { groupId, tokens: [ ...selection.tokens ] }
      const result = await assignCodesToGroup(request)
      setTarget("")
      setNewName("")
      onMoved({ ...result, groupName })
    } catch (e) {
      setError(cleanMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const canMove = count > 0 && target && (target !== NEW_GROUP || newName.trim()) && !busy

  return (
    <div className="mb-3 rounded-lg border border-voltage/60 bg-voltage-pale dark:bg-voltage/10 px-3 py-2.5" role="region" aria-label="Selected codes">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm font-semibold text-ink dark:text-cloud">
          <span className="font-mono">{count.toLocaleString()}</span> selected
        </span>
        {offerAll && (
          <button type="button" onClick={onSelectAll} className="text-sm font-semibold text-voltage-deep dark:text-voltage-light underline underline-offset-2 hover:no-underline">
            Select all {total.toLocaleString()} matching
          </button>
        )}
        {selection.all && (
          <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">every code matching this view</span>
        )}

        <div className="flex flex-wrap items-center gap-2 min-[640px]:ml-auto">
          <label className="sr-only" htmlFor="move-to-group">Move to</label>
          <select
            id="move-to-group"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-1.5 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight"
          >
            <option value="">Move to…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value={NO_GROUP}>No group</option>
            <option value={NEW_GROUP}>New group…</option>
          </select>
          {target === NEW_GROUP && (
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
              placeholder="Group name"
              aria-label="New group name"
              autoFocus
              className="w-[160px] border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-1.5 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight"
            />
          )}
          <button type="button" onClick={move} disabled={!canMove}
            className="bg-voltage text-coal rounded-lg px-3.5 py-1.5 text-sm font-semibold hover:bg-voltage-dark transition disabled:opacity-50">
            {busy ? "Moving…" : "Move"}
          </button>
          {/* Absent (not disabled) while the BulkRepoint flag is off. */}
          {onRepoint && (
            <button type="button" onClick={onRepoint} disabled={count === 0 || busy}
              className="rounded-lg px-3.5 py-1.5 text-sm font-semibold border border-[color:var(--color-border)] dark:border-storm hover:border-voltage transition disabled:opacity-50">
              Change destination
            </button>
          )}
          <button type="button" onClick={onClear} className="text-sm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud px-1">
            Cancel
          </button>
        </div>
      </div>
      {error && <p role="alert" className="mt-2 text-xs text-fail">{error}</p>}
    </div>
  )
}
